// App types
export interface App {
  id: string
  name: string
  description: string | null
  health_url: string | null
  metrics_url: string | null
  ping_interval_minutes: number
  metrics_interval_minutes: number | null
  alert_on_error: boolean
  alert_on_ping_failure: boolean
  alert_on_recovery: boolean
  alert_on_regression: boolean
  alert_on_anomaly: boolean
  auto_resolve_days: number | null
  alert_profile_id: string | null
  alert_environments: string[] | null
  report_environments: string[] | null
  // Per-app channel overrides
  alert_slack_channel_override: string | null
  alert_webhook_url_override: string | null
  alert_discord_webhook_override: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface AppWithStats extends App {
  error_count_24h: number
  unresolved_errors: number
  uptime_24h: number | null
  last_ping: Ping | null
}

export type AppCreate = {
  name: string
  description?: string
  health_url?: string
  metrics_url?: string
  ping_interval_minutes?: number
}

export type AppUpdate = {
  name?: string
  description?: string
  health_url?: string
  metrics_url?: string
  ping_interval_minutes?: number
  metrics_interval_minutes?: number | null
  alert_on_error?: boolean
  alert_on_ping_failure?: boolean
  alert_on_recovery?: boolean
  alert_on_regression?: boolean
  alert_on_anomaly?: boolean
  auto_resolve_days?: number | null
  alert_profile_id?: string | null
  alert_environments?: string[]
  report_environments?: string[]
  // Per-app channel overrides
  alert_slack_channel_override?: string | null
  alert_webhook_url_override?: string | null
  alert_discord_webhook_override?: string | null
  is_active?: boolean
}

// Error types
export const ERROR_GROUP_STATUSES = ['open', 'resolved', 'ignored', 'muted', 'regressed'] as const
export type ErrorGroupStatus = (typeof ERROR_GROUP_STATUSES)[number]

export interface ErrorGroup {
  id: string
  app_id: string
  fingerprint: string
  message: string
  level: string
  first_seen_at: string
  last_seen_at: string
  occurrence_count: number
  is_resolved: boolean
  resolved_at: string | null
  // Issue management
  assigned_to: string | null
  status: ErrorGroupStatus
  muted_until: string | null
  ignore_until_count: number | null
  environments: string[]
  users_affected_count: number
}

// Breadcrumb types
export type BreadcrumbType = 'http' | 'console' | 'click' | 'navigation' | 'custom'
export type BreadcrumbCategory =
  | 'fetch'
  | 'xhr'
  | 'log'
  | 'warn'
  | 'error'
  | 'info'
  | 'debug'
  | 'ui.click'
  | 'navigation'
  | string

export interface Breadcrumb {
  type: BreadcrumbType
  category: string | null
  message: string
  timestamp: string // ISO format
  data: Record<string, unknown> | null
}

export interface ErrorInstance {
  id: string
  app_id: string
  error_group_id: string
  message: string
  level: string
  stack_trace: string | null
  context: Record<string, unknown> | null
  environment: string | null
  release: string | null
  user_id_context: string | null
  ip_address: string | null
  breadcrumbs: Breadcrumb[] | null
  created_at: string
}

// Ping types
export type PingRegion = 'us_east' | 'us_west' | 'eu_west' | 'asia_pacific'

export const PING_REGIONS: { value: PingRegion; label: string }[] = [
  { value: 'us_east', label: 'US East' },
  { value: 'us_west', label: 'US West' },
  { value: 'eu_west', label: 'EU West' },
  { value: 'asia_pacific', label: 'Asia Pacific' },
]

export interface Ping {
  id: string
  app_id: string
  is_success: boolean
  status_code: number | null
  response_time_ms: number | null
  error_message: string | null
  region: PingRegion | null
  created_at: string
}

export interface Incident {
  id: string
  app_id: string
  cause: string
  created_at: string
  resolved_at: string | null
}

export interface UptimeStats {
  total_pings: number
  successful_pings: number
  uptime_percentage: number | null
  avg_response_time_ms: number | null
  active_incidents: number
  current_status: boolean | null
}

export interface RegionalUptimeStats {
  region: PingRegion
  total_pings: number
  successful_pings: number
  uptime_percentage: number | null
  avg_response_time_ms: number | null
  current_status: boolean | null
}

export interface RegionalUptimeStatsResponse {
  regions: Record<string, RegionalUptimeStats>
}

// Metric types
export interface MetricSnapshot {
  id: string
  app_id: string
  metrics: Record<string, number>
  created_at: string
}

export type MetricCondition =
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'eq' // Threshold-based
  | 'anomaly_low'
  | 'anomaly_medium'
  | 'anomaly_high'
  | 'anomaly_critical' // Anomaly-based

export interface MetricAlert {
  id: string
  app_id: string
  metric_name: string
  condition: MetricCondition
  threshold: number | null // null for anomaly-based conditions
  is_active: boolean
  created_at: string
}

export interface MetricSummary {
  metric_name: string
  min: number | null
  max: number | null
  avg: number | null
  latest: number | null
  data_points: number
}

export interface AvailableMetricsResponse {
  metrics: string[]
  builtin: string[]
  custom: string[]
}

export interface MetricTrend {
  timestamp: string
  value: number
  min_value: number
  max_value: number
}

export interface MetricTrendsResponse {
  metric_name: string
  trends: MetricTrend[]
  baseline: number | null
  upper_bound: number | null
  lower_bound: number | null
}

// Alert types
export interface AlertHistory {
  id: string
  team_id: string
  resource_id: string | null // app_id
  alert_type: string
  channels_sent: string[]
  title: string
  message: string
  metadata: {
    app_id?: string
    app_name?: string
    error_group_id?: string
    error_message?: string
    level?: string
    health_url?: string
    status_code?: number | null
    ping_id?: string
    metric_key?: string
    [key: string]: unknown
  }
  sent_at: string
}

export interface NotificationPreference {
  id: string
  user_id: string
  email_on_error: boolean
  email_on_ping_failure: boolean
  email_on_recovery: boolean
  email_on_metric_alert: boolean
  weekly_digest: boolean
  cooldown_minutes: number
  created_at: string
  updated_at: string
}

export interface NotificationPreferenceUpdate {
  email_on_error?: boolean
  email_on_ping_failure?: boolean
  email_on_recovery?: boolean
  email_on_metric_alert?: boolean
  weekly_digest?: boolean
  cooldown_minutes?: number
}

// Webhook types
export interface Webhook {
  id: string
  user_id: string
  name: string
  url: string
  is_active: boolean
  on_error: boolean
  on_ping_failure: boolean
  on_recovery: boolean
  on_metric_alert: boolean
  created_at: string
  updated_at: string
}

export interface WebhookCreate {
  name: string
  url: string
  secret?: string
  on_error?: boolean
  on_ping_failure?: boolean
  on_recovery?: boolean
  on_metric_alert?: boolean
}

export interface WebhookUpdate {
  name?: string
  url?: string
  secret?: string
  is_active?: boolean
  on_error?: boolean
  on_ping_failure?: boolean
  on_recovery?: boolean
  on_metric_alert?: boolean
}

export interface WebhookListResponse {
  webhooks: Webhook[]
}

// Note: Integration types are now in @roselabs-io/saas-shell notifications module

// Dashboard types
export interface DashboardAppStatus {
  id: string
  name: string
  error_count_24h: number
  unresolved_errors: number
  is_healthy: boolean | null
  uptime_24h: number | null
}

export interface DashboardOverview {
  total_apps: number
  total_errors_24h: number
  overall_uptime: number | null
  apps_with_issues: number
  anomaly_count_24h: number
  apps: DashboardAppStatus[]
}

export interface ErrorTrendItem {
  date: string
  count: number
}

export interface ErrorTrendResponse {
  trend: ErrorTrendItem[]
  total: number
}

export interface RecentAlert {
  id: string
  app_id: string
  alert_type: string
  subject: string
  sent_at: string
}

export interface IncidentSummary {
  id: string
  app_id: string
  app_name: string
  cause: string
  created_at: string
  resolved_at: string | null
}

// Email recipient types
export interface EmailRecipient {
  id: string
  email: string
  created_at: string
}

export interface EmailRecipientsResponse {
  recipients: EmailRecipient[]
}

// Status page types
export interface StatusPage {
  id: string
  app_id: string
  user_id: string
  slug: string
  title: string
  description: string | null
  logo_url: string | null
  primary_color: string
  background_color: string
  show_uptime: boolean
  show_response_time: boolean
  show_incident_history: boolean
  incident_history_days: number
  custom_domain: string | null
  custom_domain_verified: boolean
  is_public: boolean
  created_at: string
  updated_at: string
}

export interface StatusPageWithApp extends StatusPage {
  app_name: string
}

export interface StatusPageCreate {
  app_id: string
  title: string
  slug: string
  description?: string
  logo_url?: string
  primary_color?: string
  background_color?: string
  show_uptime?: boolean
  show_response_time?: boolean
  show_incident_history?: boolean
  incident_history_days?: number
  is_public?: boolean
}

export interface StatusPageUpdate {
  title?: string
  slug?: string
  description?: string | null
  logo_url?: string | null
  primary_color?: string
  background_color?: string
  show_uptime?: boolean
  show_response_time?: boolean
  show_incident_history?: boolean
  incident_history_days?: number
  is_public?: boolean
  custom_domain?: string | null
}

export interface StatusPageListResponse {
  status_pages: StatusPageWithApp[]
  total: number
}

export interface SlugCheckResponse {
  available: boolean
  slug: string
}

// Public status page types
export interface StatusPagePublicInfo {
  title: string
  description: string | null
  logo_url: string | null
  primary_color: string
  background_color: string
}

export interface CurrentStatus {
  status: 'operational' | 'degraded' | 'down' | 'unknown'
  message: string
  last_checked: string | null
}

export interface ResponseTimePoint {
  timestamp: string
  response_time_ms: number
}

export interface IncidentHistoryItem {
  id: string
  started_at: string
  ended_at: string | null
  duration_seconds: number | null
  cause: string
  is_resolved: boolean
}

export interface PublicStatusPageResponse {
  status_page: StatusPagePublicInfo
  current_status: CurrentStatus
  uptime_stats: Record<string, number | null> | null
  response_time_data: ResponseTimePoint[] | null
  incident_history: IncidentHistoryItem[] | null
}

// App notification settings types
export interface AppNotificationSettings {
  id: string
  app_id: string
  user_id: string
  notifications_enabled: boolean
  email_on_error: boolean | null
  email_on_ping_failure: boolean | null
  email_on_recovery: boolean | null
  email_on_metric_alert: boolean | null
  quiet_hours_enabled: boolean
  quiet_hours_start: string | null
  quiet_hours_end: string | null
  quiet_hours_timezone: string
  escalation_enabled: boolean
  escalation_delay_minutes: number
  escalation_email: string | null
  created_at: string
  updated_at: string
}

export interface AppNotificationSettingsWithApp extends AppNotificationSettings {
  app_name: string
}

export interface AppNotificationSettingsUpdate {
  notifications_enabled?: boolean
  email_on_error?: boolean | null
  email_on_ping_failure?: boolean | null
  email_on_recovery?: boolean | null
  email_on_metric_alert?: boolean | null
  quiet_hours_enabled?: boolean
  quiet_hours_start?: string | null
  quiet_hours_end?: string | null
  quiet_hours_timezone?: string
  escalation_enabled?: boolean
  escalation_delay_minutes?: number
  escalation_email?: string | null
}

export interface AppNotificationSettingsListResponse {
  settings: AppNotificationSettingsWithApp[]
  total: number
}

// Analytics types
export interface ResponseTimeTrend {
  timestamp: string | null
  avg_response_time: number | null
  min_response_time: number | null
  max_response_time: number | null
  p95_response_time: number | null
  sample_count: number
}

export interface ErrorRateTrend {
  timestamp: string | null
  error_count: number
  unique_errors: number
}

export interface UptimeAnalytics {
  uptime_percentage: number | null
  total_pings: number
  successful_pings: number
  failed_pings: number
  incident_count: number
  total_downtime_seconds: number
  avg_incident_duration_seconds: number | null
  mttr_seconds: number | null
}

export interface UptimeTrend {
  timestamp: string | null
  uptime_percentage: number | null
  total_pings: number
  successful_pings: number
}

export interface DashboardSummary {
  app_count: number
  total_errors_24h: number
  total_errors_7d: number
  avg_response_time_24h: number | null
  avg_uptime_7d: number | null
  active_incidents: number
}

// Anomaly detection types
export interface PerformanceBaseline {
  id: string
  app_id: string
  metric_name: string
  baseline_value: number
  std_deviation: number
  min_value: number
  max_value: number
  sample_count: number
  period_days: number
  calculated_at: string
}

export interface AnomalyDetection {
  id: string
  app_id: string
  metric_name: string
  detected_value: number
  baseline_value: number
  deviation: number
  severity: 'low' | 'medium' | 'high' | 'critical'
  is_acknowledged: boolean
  acknowledged_at: string | null
  created_at: string
}

export interface AnomalyDetectionWithApp extends AnomalyDetection {
  app_name: string
}

export interface AnomaliesListResponse {
  anomalies: AnomalyDetectionWithApp[]
  total: number
}

export interface MetricDataPoint {
  timestamp: string
  value: number | null
}

export interface AnomalyContextError {
  id: string
  message: string
  level: string
  count: number
  timestamp: string
}

export interface ErrorEvent {
  timestamp: string
  count: number
  level: string
}

export interface AlertEvent {
  timestamp: string
  type: string
  title: string
}

export interface AnomalyContext {
  anomaly_id: string
  app_id: string
  app_name: string
  metric_name: string
  detected_value: number
  baseline_value: number
  std_deviation: number
  deviation: number
  severity: string
  detected_at: string
  upper_bound: number
  lower_bound: number
  metric_trends: MetricDataPoint[]
  error_events: ErrorEvent[]
  alert_events: AlertEvent[]
  error_count_window: number
  alert_count_window: number
  recent_errors: AnomalyContextError[]
}

// Reporting types
export interface ScheduledReport {
  id: string
  user_id: string
  name: string
  report_type: 'summary' | 'detailed' | 'sla'
  frequency: 'daily' | 'weekly' | 'monthly'
  app_ids: string[] | null
  recipients: string[]
  include_charts: boolean
  is_active: boolean
  last_sent_at: string | null
  next_send_at: string | null
  created_at: string
  updated_at: string
}

export interface ScheduledReportCreate {
  name: string
  report_type: string
  frequency: string
  app_ids?: string[]
  recipients: string[]
  include_charts?: boolean
}

export interface ScheduledReportUpdate {
  name?: string
  report_type?: string
  frequency?: string
  app_ids?: string[]
  recipients?: string[]
  include_charts?: boolean
  is_active?: boolean
}

export interface ScheduledReportsListResponse {
  reports: ScheduledReport[]
  total: number
}

export interface ReportHistory {
  id: string
  scheduled_report_id: string | null
  user_id: string
  report_type: string
  format: string
  file_path: string | null
  sent_to: string[] | null
  status: 'pending' | 'completed' | 'failed'
  error_message: string | null
  created_at: string
}

export interface ReportHistoryListResponse {
  history: ReportHistory[]
  total: number
}

export interface SLAReport {
  period: {
    start: string
    end: string
  }
  uptime: {
    percentage: number | null
    total_checks: number
    successful_checks: number
  }
  response_time: {
    avg_ms: number | null
    p50_ms: number | null
    p95_ms: number | null
    p99_ms: number | null
  }
  incidents: {
    count: number
    total_downtime_seconds: number
  }
  sla_compliance: Record<string, boolean>
}

// Scheduled Reports (platform-level)
export type ReportFrequency = 'daily' | 'weekly' | 'monthly'

export interface ReportConfiguration {
  id: string
  team_id: string
  product_slug: string
  name: string
  enabled: boolean
  frequency: ReportFrequency
  day_of_week: number | null
  day_of_month: number | null
  delivery_hour: number
  timezone: string
  include_all_apps: boolean
  app_ids: string[] | null
  environments: string[]
  include_errors: boolean
  include_uptime: boolean
  include_metrics: boolean
  include_anomalies: boolean
  recipients: string[]
  created_at: string
  updated_at: string
  last_sent_at: string | null
}

export interface CreateReportConfiguration {
  name: string
  enabled?: boolean
  frequency?: ReportFrequency
  day_of_week?: number
  day_of_month?: number
  delivery_hour?: number
  timezone?: string
  include_all_apps?: boolean
  app_ids?: string[]
  environments?: string[]
  include_errors?: boolean
  include_uptime?: boolean
  include_metrics?: boolean
  include_anomalies?: boolean
  recipients?: string[]
}

export interface UpdateReportConfiguration {
  name?: string
  enabled?: boolean
  frequency?: ReportFrequency
  day_of_week?: number
  day_of_month?: number
  delivery_hour?: number
  timezone?: string
  include_all_apps?: boolean
  app_ids?: string[]
  environments?: string[]
  include_errors?: boolean
  include_uptime?: boolean
  include_metrics?: boolean
  include_anomalies?: boolean
  recipients?: string[]
}

export interface ReportConfigurationList {
  configurations: ReportConfiguration[]
  total: number
}

export interface ReportHistoryItem {
  id: string
  configuration_id: string
  team_id: string
  product_slug: string
  period_start: string
  period_end: string
  frequency: string
  sent_at: string
  recipients: string[]
  status: 'sent' | 'failed'
  error_message: string | null
}

export interface ReportHistoryList {
  reports: ReportHistoryItem[]
  total: number
}

export interface SendReportResponse {
  success: boolean
  report_history_id: string | null
  error: string | null
}

// Cron monitoring types
export type CronMonitorStatus = 'ok' | 'missed' | 'disabled'
export type CronRunStatus = 'ok' | 'error' | 'in_progress'

export interface CronMonitor {
  id: string
  app_id: string
  name: string
  slug: string
  schedule: string
  expected_interval_minutes: number
  grace_period_minutes: number
  last_heartbeat_at: string | null
  next_expected_at: string | null
  status: CronMonitorStatus
  is_active: boolean
  description: string | null
  alert_on_failure: boolean
  created_at: string
  updated_at: string
}

export interface CronMonitorCreate {
  name: string
  slug: string
  schedule: string
  expected_interval_minutes: number
  grace_period_minutes?: number
  description?: string
  alert_on_failure?: boolean
}

export interface CronMonitorUpdate {
  name?: string
  schedule?: string
  expected_interval_minutes?: number
  grace_period_minutes?: number
  description?: string
  alert_on_failure?: boolean
  is_active?: boolean
}

export interface CronMonitorListResponse {
  monitors: CronMonitor[]
  total: number
}

export interface CronRun {
  id: string
  monitor_id: string
  started_at: string
  duration_ms: number | null
  status: CronRunStatus
  metadata: Record<string, unknown> | null
  environment: string | null
}

export interface CronRunListResponse {
  runs: CronRun[]
  total: number
}

export interface HeartbeatURLResponse {
  url: string
  slug: string
  curl_example: string
  sdk_example_python: string
  sdk_example_js: string
}

// Inbound Filter types
export type FilterAction = 'drop' | 'sample'
export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'matches_regex'
  | 'is_set'
  | 'is_not_set'
export type FilterField = 'message' | 'level' | 'environment' | 'release' | 'user_id'

export interface FilterCondition {
  field: string
  operator: FilterOperator
  value: string | null
}

export interface InboundFilter {
  id: string
  app_id: string
  name: string
  description: string | null
  action: FilterAction
  sample_rate: number | null
  conditions: FilterCondition[]
  priority: number
  is_active: boolean
  matched_count: number
  filtered_count: number
  last_matched_at: string | null
  created_at: string
  updated_at: string
}

export interface InboundFilterCreate {
  name: string
  description?: string
  action?: FilterAction
  sample_rate?: number
  conditions: FilterCondition[]
  priority?: number
  is_active?: boolean
}

export interface InboundFilterUpdate {
  name?: string
  description?: string | null
  action?: FilterAction
  sample_rate?: number | null
  conditions?: FilterCondition[]
  priority?: number
  is_active?: boolean
}

export interface InboundFilterListResponse {
  filters: InboundFilter[]
  total: number
}

export interface FilterTestRequest {
  message: string
  level?: string
  environment?: string
  release?: string
  user_id?: string
  context?: Record<string, unknown>
}

export interface FilterTestResult {
  matched: boolean
  action: string | null
  would_keep: boolean
  matched_conditions: string[]
  unmatched_conditions: string[]
}
